import { MutableRefObject, RefObject, useEffect, useState } from "react";

/**
 * Helper to extract element from ref or direct element
 */
function getElement<T extends HTMLElement | undefined | null>(
  target: RefObject<T> | MutableRefObject<T> | T
): T | null {
  if (target && typeof target === 'object' && 'current' in target) {
    return target.current;
  }
  return target as T | null;
}

/**
 * Hook to observe the size of an element
 * @param target The element to observe - can be a RefObject, MutableRefObject, or direct element
 * @returns The dimensions of the element
 */
export const useResizeObserver = <T extends HTMLElement | undefined | null>(
  target: RefObject<T> | MutableRefObject<T> | T,
  debug?: boolean,
  label?: string
) => {
  const [dimensions, setDimensions] = useState<DOMRectReadOnly | undefined>(undefined);

  // Extract element - for direct elements this triggers effect on change
  const element = getElement(target);

  useEffect(() => {
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (debug) {
        console.log(`[useResizeObserver${label ? `:${label}` : ""}] Resize observed:`, entries);
      }

      const rec = {
        ...entries[0].contentRect
      }
      rec.width = entries[0].borderBoxSize[0].inlineSize;
      rec.height = entries[0].borderBoxSize[0].blockSize
      setDimensions(rec);
    });

    resizeObserver.observe(element, { box: "border-box" });

    return () => {
      resizeObserver.disconnect();
    };
  }, [element, debug, label]);

  return dimensions;
}